import { gql, type TypedDocumentNode } from '@apollo/client';
import { useNavigate } from 'react-router';

import { Centered } from '@sorare/core/src/atoms/layout/Centered/index.tsx';
import {
  ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE,
  ANY_SPORT_PLAY_BOARDS_PRO,
} from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { BoardPicker } from '#src/components/pickers/BoardPicker/index.tsx';
import { Mode, ModePicker } from '#src/components/pickers/ModePicker/index.tsx';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';
import { modeToArenaMode } from '#src/lib/gameMode.ts';

import type { ProBoardPickers_careerBoard } from './__generated__/index.graphql.ts';

type Props = {
  boards: readonly ProBoardPickers_careerBoard[];
  selectedBoardId?: string;
  onSelect: (args: { boardId: string; taskId: string }) => void;
};

export const ProBoardPickers = ({
  boards,
  selectedBoardId,
  onSelect,
}: Props) => {
  const navigate = useNavigate();
  const { sport, generateSportContextPath } = useSportContext();
  const {
    flags: {
      enableSorare27Nav = { BASEBALL: false, FOOTBALL: false, NBA: false },
    },
  } = useFeatureFlags();
  const { updateBoardView } = useLastVisitedBoardView();
  const showModePicker = enableSorare27Nav[sport];

  const careerBoards = boards.filter(
    isTypeFn<(typeof boards)[number], 'CareerBoard'>('CareerBoard')
  );

  return (
    <Centered
      style={{
        alignItems: 'flex-start',
        gap: 'var(--half-unit)',
      }}
      right={
        !!showModePicker && (
          <BoardPicker
            currentBoardId={selectedBoardId}
            boards={careerBoards}
            size="sm"
            highlighted={false}
            orientation="down"
            onSelect={onSelect}
          />
        )
      }
    >
      {showModePicker ? (
        <ModePicker
          onSelect={mode => {
            updateBoardView({ lastVisitedMode: mode });
            if (mode === Mode.PRO) {
              navigate(generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO));
            } else {
              const arenaMode = modeToArenaMode(mode);
              navigate(
                generateSportContextPath(
                  ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE,
                  {
                    params: { arenaMode },
                  }
                )
              );
            }
          }}
        />
      ) : (
        <BoardPicker
          currentBoardId={selectedBoardId}
          boards={careerBoards}
          size="md"
          highlighted
          orientation="up"
          onSelect={onSelect}
        />
      )}
    </Centered>
  );
};

ProBoardPickers.fragments = {
  careerBoard: gql`
    fragment ProBoardPickers_careerBoard on CareerBoard {
      id
      ...BoardPicker_careerBoard
    }
    ${BoardPicker.fragments.careerBoard}
  ` as TypedDocumentNode<ProBoardPickers_careerBoard>,
};
